// src/api/v3/vouchers-api-v3.js
import { authHeaders } from './_auth';

const API_BASE   = process.env.REACT_APP_API_BASE;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || "/api/v3";
const B = (p = "") => `${API_BASE}${API_PREFIX}${p}`;

function toQS(params = {}) {
  const s = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) v.forEach((x) => s.append(k, x));
    else s.set(k, v);
  });
  const str = s.toString();
  return str ? `?${str}` : "";
}

// src/api/v3/vouchers-api-v3.js
async function jsonFetch(path, { method = "GET", body, headers = {}, ...rest } = {}) {
  const url = B(path);
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json", ...headers },
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
    ...rest,
  });
  const text = await res.text().catch(() => "");
  if (!res.ok) {
    let data;
    try { data = text ? JSON.parse(text) : undefined; } catch {}
    const err = new Error(`API ${method} ${path} failed: ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  try { return text ? JSON.parse(text) : null; } catch { return null; }
}


async function formFetch(path, formData, { method = "POST", headers = {}, ...rest } = {}) {
  const url = B(path);
  const res = await fetch(url, {
    method,
    headers, // let the browser set the multipart boundary
    body: formData,
    credentials: "include",
    ...rest,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let data;
    try { data = text ? JSON.parse(text) : undefined; } catch {}
    const detail = data?.error || (Array.isArray(data?.errors) ? data.errors.join("\n") : text);
    throw new Error(`API ${method} ${path} failed: ${res.status}${detail ? ` ${detail}` : ""}`);
  }
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  return null;
}

// --- URL builders for CSV exports -------------------------------------------------
export const exportVouchersURL = (params = {}) => B(`/vouchers/export.csv${toQS(params)}`);
export const exportVoucherPerformanceURL = ({ start, end } = {}) =>
  B(`/vouchers/performance.csv${toQS({ start_date: start, end_date: end })}`);

// --- Normalizers -----------------------------------------------------------------
const isTrue = (v) => {
  if (v === true || v === 1) return true;
  const s = String(v).toLowerCase();
  return s === "1" || s === "true" || s === "t" || s === "yes" || s === "y";
};

export const normalizeVoucher = (row) => {
  const x = row?.voucher ?? row ?? {};
  return {
    id: x.id,
    code: x.code || "",
    discount_type: x.discount_type || "",
    discount_value: Number(x.discount_value ?? 0),
    applies_to_scope: x.applies_to_scope || "all_items",
    active: isTrue(x.active),
    expiration_date: x.expiration_date || null,
    max_redemption_per_user: Number(x.max_redemption_per_user ?? 1),
    is_promoted: isTrue(x.is_promoted),
    promotional_text: x.promotional_text || "",
    // pass through any optional targeting fields if backend sends them
    category_codes: x.category_codes || x.product_category_slugs || [],
    order_category_codes: x.order_category_codes || x.order_category_slugs || [],
    created_at: x.created_at,
  };
};

// --- Public API ------------------------------------------------------------------
const VouchersAPI = {
  // Raw list (may return {items:[...]} or [...] depending on backend)
  list: (params = {}) => jsonFetch(`/vouchers${toQS(params)}`),

  // Array only (backward-friendly): returns []
  listItems: async (params = {}) => {
    const data = await VouchersAPI.list(params);
    return Array.isArray(data) ? data : (data?.items || []);
  },

  // Normalized array for UI
  listNormalized: async (params = {}) => {
    const items = await VouchersAPI.listItems(params);
    return items.map(normalizeVoucher);
  },

  show: (id) => jsonFetch(`/vouchers/${id}`), // expected { item: {...} }
  create: (payload) => jsonFetch(`/vouchers`, { method: "POST", body: payload, headers: authHeaders() }),
  update: (id, payload) => jsonFetch(`/vouchers/${id}`, { method: "PUT", body: payload }),
  destroy: (id) => jsonFetch(`/vouchers/${id}`, { method: "DELETE" }),

  apply: (orderId, code) =>
    jsonFetch(`/vouchers/apply`, { method: "POST", body: { order_id: orderId, code } }),

  importCSV: (file, defaults = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(defaults).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, String(v));
    });
    return formFetch(`/vouchers/import`, fd);
  },

  exportURL: (params = {}) => exportVouchersURL(params),
  exportPerformanceURL: (params = {}) => exportVoucherPerformanceURL(params),
};

export default VouchersAPI;
